import React, { useState, useEffect, useCallback } from 'react';

import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { LoadingState, EmptyState, Pagination, ConfirmationModal } from '../../components/common';
import { Button } from '../../components/common/Button';
import { Card, CardContent } from '../../components/common/Card';
import { DistrictForm } from '../../components/districts/DistrictForm';
import { Modal } from '../../components/modals/Modal';
import { Table, TableColumn } from '../../components/tables/Table';
import { APP_PERMISSIONS } from '../../constants';
import { usePermissions } from '../../hooks/usePermissions';
import { 
  District,
  getDistricts,
  createDistrict,
  updateDistrict,
  deleteDistrict
} from '../../services/districtsService';
import { DistrictCreateFormData, DistrictUpdateFormData } from '../../types';

interface SearchForm {
  search: string;
}

export const DistrictsPage: React.FC = () => {
  const { hasPermission } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [districts, setDistricts] = useState<District[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDistrict, setEditingDistrict] = useState<District | null>(null);
  const [deletingDistrict, setDeletingDistrict] = useState<District | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    pages: 1,
    has_next: false,
    has_prev: false,
    next_page: null as number | null,
    prev_page: null as number | null
  });
  const [itemsPerPage] = useState(10); // 10 items per page
  
  // Get current page and search from URL params
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const urlSearchTerm = searchParams.get('search') || '';
  
  // Search form
  const { register, handleSubmit, reset } = useForm<SearchForm>({
    defaultValues: { search: urlSearchTerm }
  });

  // Load districts
  const loadDistricts = useCallback(async (
    page: number = currentPage,
    search: string = urlSearchTerm
  ) => {
    try {
      setLoading(true);
      setError(null);
      
      const skip = (page - 1) * itemsPerPage;
      const response = await getDistricts({ 
        skip, 
        limit: itemsPerPage,
        search: search || undefined
      });
      
      // Handle the new paginated response structure
      const districtsData = response.data || [];
      const paginationData = response.pagination || {
        page: 1,
        per_page: 10,
        total: 0,
        pages: 1,
        has_next: false,
        has_prev: false,
        next_page: null,
        prev_page: null,
      };
      
      setDistricts(districtsData);
      setPagination(paginationData);
    } catch (err) {
      setError('Failed to load districts');
      console.error('Error loading districts:', err);
      setDistricts([]);
      setPagination({
        page: 1,
        per_page: 10,
        total: 0,
        pages: 1,
        has_next: false,
        has_prev: false,
        next_page: null,
        prev_page: null
      });
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, urlSearchTerm]);

  useEffect(() => {
    loadDistricts();
  }, [loadDistricts]);

  // Handle search form submission
  const handleSearch = (data: SearchForm) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (data.search.trim()) {
      newSearchParams.set('search', data.search.trim());
    } else {
      newSearchParams.delete('search');
    }
    newSearchParams.set('page', '1'); // Reset to first page when searching
    setSearchParams(newSearchParams);
  };

  // Remove debounced search - only search on button click

  // Handle page change
  const handlePageChange = (page: number) => {
    if (page !== currentPage) {
      // Update URL params
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('page', page.toString());
      setSearchParams(newSearchParams);
    }
  };

  // Table columns
  const columns: TableColumn<District>[] = [
    {
      key: 'name',
      header: 'District Name',
      accessor: 'name',
    },
    {
      key: 'code',
      header: 'Code',
      accessor: 'code',
    },
    {
      key: 'country',
      header: 'Country',
      accessor: 'country',
    },
    {
      key: 'state',
      header: 'State',
      accessor: 'state',
    },
    {
      key: 'city',
      header: 'City',
      accessor: (district: District) => district.city || '-',
    },
    {
      key: 'superintendent',
      header: 'Superintendent',
      accessor: (district: District) => district.superintendent || '-',
    },
    {
      key: 'email',
      header: 'Email',
      accessor: (district: District) => district.email || '-',
    },
    {
      key: 'actions',
      header: 'Actions',
      accessor: (district) => (
        <div className="flex items-center space-x-2">
          {/* {hasPermission(APP_PERMISSIONS.READ_DISTRICT) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleView(district)}
              className="h-8 w-8 p-0"
            >
              👁️
            </Button>
          )} */}
          {hasPermission(APP_PERMISSIONS.UPDATE_DISTRICT) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleEdit(district)}
              className="h-8 w-8 p-0"
            >
              ✏️
            </Button>
          )}
          {hasPermission(APP_PERMISSIONS.DELETE_DISTRICT) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(district)}
              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
            >
              🗑️
            </Button>
          )}
        </div>
      ),
      width: '5%',
      align: 'center',
    },
  ];

  // Event handlers
  const handleCreate = () => {
    setEditingDistrict(null);
    setIsModalOpen(true);
  };

  const handleEdit = (district: District) => {
    setEditingDistrict(district);
    setIsModalOpen(true);
  };

  // const handleView = (district: District) => {
  //   // TODO: Implement view functionality
  //   toast.info(`Viewing district: ${district.name}`);
  // };

  const handleDelete = (district: District) => {
    setDeletingDistrict(district);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingDistrict(null);
  };

  const handleFormSubmit = async (formData: DistrictCreateFormData | DistrictUpdateFormData) => {
    try {
      setIsSubmitting(true);
      
      if (editingDistrict) {
        // Update district
        await updateDistrict(editingDistrict.id, formData as DistrictUpdateFormData);
        toast.success('District updated successfully');
      } else {
        // Create district
        await createDistrict(formData as DistrictCreateFormData);
        toast.success('District created successfully');
      }
      
      handleModalClose();
      loadDistricts();
    } catch (err) {
      toast.error('Failed to save district');
      console.error('Error saving district:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deletingDistrict) return;
    
    try {
      await deleteDistrict(deletingDistrict.id);
      toast.success('District deleted successfully');
      setDeletingDistrict(null);
      loadDistricts();
    } catch (err) {
      toast.error('Failed to delete district');
      console.error('Error deleting district:', err);
    }
  };

  const canCreate = hasPermission(APP_PERMISSIONS.CREATE_DISTRICT);
  const canRead = hasPermission(APP_PERMISSIONS.READ_DISTRICT);


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Districts Management</h1>
          <p className="mt-2 text-gray-600">
            Manage school districts and their information
          </p>
        </div>
        {canCreate && (
          <Button
            variant="primary"
            onClick={handleCreate}
            size="md"
          >
            + Add District
          </Button>
        )}
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent>
          <form onSubmit={handleSubmit(handleSearch)} className="flex gap-4">
            <div className="flex-1">
              <input
                {...register('search')}
                type="text"
                placeholder="Search districts by name, code, country, or state..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                defaultValue={urlSearchTerm}
              />
            </div>
            <Button type="submit" variant="primary" size="md" disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </Button>
            {urlSearchTerm && (
              <Button 
                type="button" 
                variant="secondary" 
                onClick={() => {
                  reset({ search: '' });
                  const newSearchParams = new URLSearchParams(searchParams);
                  newSearchParams.delete('search');
                  newSearchParams.set('page', '1');
                  setSearchParams(newSearchParams);
                }}
              >
                Clear
              </Button>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Districts Table */}
      {canRead ? (
        <div className="space-y-4">
          <Card>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingState message="Loading districts..." />
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <div className="text-red-600 mb-4">
                    <h3 className="text-lg font-semibold">Failed to load districts</h3>
                    <p className="text-sm">{error}</p>
                  </div>
                  <Button onClick={() => loadDistricts()} variant="secondary">
                    Try Again
                  </Button>
                </div>
              ) : districts.length === 0 ? (
                <EmptyState
                  title="No districts found"
                  description="There are no districts to display. Create your first district to get started."
                  icon="🏢"
                  actionText={canCreate ? "Add District" : undefined}
                  onAction={canCreate ? handleCreate : undefined}
                />
              ) : (
                <Table
                  data={districts}
                  columns={columns}
                  keyField="id"
                />
              )}
            </CardContent>
          </Card>
          
          {/* Pagination - Below the table */}
          {!loading && !error && (
            <div className="bg-white px-4 py-3 border border-gray-200 rounded-lg">
              <Pagination
                pagination={pagination}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      ) : (
        <Card>
          <CardContent>
            <div className="text-center py-8 text-gray-500">
              You don't have permission to view districts.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title={editingDistrict ? 'Edit District' : 'Create District'}
        size="xl"
      >
        <DistrictForm
          district={editingDistrict || undefined}
          onSubmit={handleFormSubmit}
          onCancel={handleModalClose}
          isLoading={isSubmitting}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!deletingDistrict}
        onClose={() => setDeletingDistrict(null)}
        onConfirm={confirmDelete}
        title="Delete District"
        message={`Are you sure you want to delete ${deletingDistrict?.name}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isSubmitting}
      />
    </div>
  );
};

export default DistrictsPage;


